import { useEffect, useRef } from "react";
import { useAppStore } from "@/store/appStore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import clsx from "clsx";

/* ── Type meta ────────────────────────────────────────────────────────────── */
const TYPE_META = {
  achievement: { color: "#fbbf24", bg: "rgba(251,191,36,0.10)",  icon: "🏅", label: "Achievement" },
  message:     { color: "#06b6d4", bg: "rgba(6,182,212,0.10)",   icon: "💬", label: "Nachricht"   },
  reward:      { color: "#a78bfa", bg: "rgba(139,92,246,0.10)",  icon: "⚡", label: "Belohnung"   },
  system:      { color: "#94a3b8", bg: "rgba(148,163,184,0.07)", icon: "🔔", label: "System"      },
  streak:      { color: "#fb923c", bg: "rgba(249,115,22,0.10)",  icon: "🔥", label: "Streak"      },
};

/* ── Keyframes (injected once) ────────────────────────────────────────────── */
const NOTIF_KF = `
@keyframes nd-in  { from{opacity:0;transform:scale(.94) translateY(-10px)} to{opacity:1;transform:scale(1) translateY(0)} }
@keyframes nd-out { from{opacity:1;transform:scale(1)}                     to{opacity:0;transform:scale(.94) translateY(-6px)} }
@keyframes nd-bd  { from{opacity:0} to{opacity:1} }
@keyframes nd-row { from{opacity:0;transform:translateX(8px)} to{opacity:1;transform:translateX(0)} }
`;

function injectKF() {
  if (document.getElementById("__nd_kf")) return;
  const s = document.createElement("style");
  s.id = "__nd_kf"; s.textContent = NOTIF_KF;
  document.head.appendChild(s);
}

/* ── Close button ─────────────────────────────────────────────────────────── */
function CloseBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Schließen"
      className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/8 transition-all duration-150 active:scale-90 flex-shrink-0"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M18 6 6 18M6 6l12 12"/>
      </svg>
    </button>
  );
}

/* ── Single notification row ──────────────────────────────────────────────── */
function NotifRow({ n, idx, onMarkRead }) {
  const meta  = TYPE_META[n.type] || TYPE_META.system;
  const delay = Math.min(idx * 40, 200);

  return (
    <div
      className={clsx(
        "group relative flex gap-3 px-4 py-3.5 cursor-default select-none",
        "transition-colors duration-150",
        !n.isRead ? "bg-violet-500/[0.04] hover:bg-violet-500/[0.07]" : "hover:bg-white/[0.02]",
        idx !== 0 && "border-t border-white/[0.04]"
      )}
      style={{ animation: `nd-row .3s ease ${delay}ms both` }}
      onDoubleClick={() => !n.isRead && onMarkRead?.(n.id)}
    >
      {/* Unread accent bar */}
      {!n.isRead && (
        <div
          className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full"
          style={{ background: `linear-gradient(180deg, ${meta.color}, ${meta.color}55)`, boxShadow: `0 0 6px ${meta.color}60` }}
        />
      )}

      {/* Icon badge */}
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 mt-0.5"
        style={{ background: meta.bg, border: `1px solid ${meta.color}30` }}
      >
        {n.emoji || meta.icon}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-start justify-between gap-2">
          <p className={clsx(
            "text-[13px] font-bold leading-tight",
            !n.isRead ? "text-white" : "text-gray-300"
          )}>
            {n.title}
          </p>
          {!n.isRead && (
            <div
              className="w-2 h-2 rounded-full flex-shrink-0 mt-1"
              style={{ background: meta.color, boxShadow: `0 0 6px ${meta.color}` }}
            />
          )}
        </div>
        {n.body && (
          <p className="text-[11px] text-gray-500 line-clamp-2 leading-relaxed">{n.body}</p>
        )}
        <div className="flex items-center gap-1.5 pt-0.5">
          <span
            className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md"
            style={{ background: `${meta.color}18`, color: meta.color }}
          >
            {meta.label}
          </span>
          <span className="text-[10px] text-gray-600">
            {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true, locale: de })}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Empty state ──────────────────────────────────────────────────────────── */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-14 px-6">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-4"
        style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.15)" }}
      >
        🔕
      </div>
      <p className="text-sm font-bold text-gray-400 text-center">Keine Benachrichtigungen</p>
      <p className="text-[11px] text-gray-600 text-center mt-1">Du bist auf dem neuesten Stand!</p>
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */
export default function NotifDrawer() {
  const { notifDrawerOpen, toggleNotifDrawer } = useAppStore();
  const qc  = useQueryClient();
  const ref = useRef(null);

  useEffect(() => { injectKF(); }, []);

  /* Close on Escape */
  useEffect(() => {
    if (!notifDrawerOpen) return;
    const onKey = (e) => { if (e.key === "Escape") toggleNotifDrawer(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [notifDrawerOpen, toggleNotifDrawer]);

  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn:  () => api.get("/notifications").then((r) => r.data),
    enabled:  notifDrawerOpen,
  });

  const readAll = useMutation({
    mutationFn: () => api.patch("/notifications/read-all"),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markOne = useMutation({
    mutationFn: (id) => api.patch(`/notifications/${id}/read`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  if (!notifDrawerOpen) return null;

  const notifs = data?.notifications || [];
  const unread = notifs.filter((n) => !n.isRead).length;

  return (
    <>
      {/* ── Full-screen backdrop – click outside to close ─────────────────── */}
      <div
        className="fixed inset-0 z-[49]"
        style={{ animation: "nd-bd .2s ease both" }}
        onClick={toggleNotifDrawer}
        aria-hidden
      />

      {/* ── Panel ─────────────────────────────────────────────────────────── */}
      <div
        ref={ref}
        role="dialog"
        aria-label="Benachrichtigungen"
        className="fixed z-50 flex flex-col"
        style={{
          /* Centered below TopBar */
          top:   "calc(56px + 8px)",
          left:  "50%",
          transform: "translateX(-50%)",
          width:  "min(440px, calc(100vw - 24px))",
          maxHeight: "min(72dvh, 580px)",

          background: "linear-gradient(145deg, rgba(14,10,36,0.98) 0%, rgba(7,7,24,0.99) 100%)",
          borderRadius: "1.5rem",
          border: "1px solid rgba(139,92,246,0.2)",
          boxShadow: [
            "0 24px 64px rgba(0,0,0,0.7)",
            "0 0 0 1px rgba(255,255,255,0.03) inset",
            "0 1px 0 rgba(255,255,255,0.06) inset",
            "0 0 60px rgba(139,92,246,0.08)",
          ].join(", "),
          animation: "nd-in .25s cubic-bezier(.22,.68,0,1.1) both",
        }}
        /* Stop click from bubbling to backdrop */
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top glow edge */}
        <div
          className="absolute top-0 left-8 right-8 h-px pointer-events-none rounded-full"
          style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.5) 30%, rgba(6,182,212,0.3) 70%, transparent)" }}
        />

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-5 py-3.5 flex-shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          <div className="flex items-center gap-2.5">
            {/* Bell icon */}
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.25)" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>

            <div>
              <h3 className="font-black text-[13px] text-white leading-tight">Benachrichtigungen</h3>
              {unread > 0 ? (
                <p className="text-[10px] text-violet-400 font-semibold">{unread} ungelesen</p>
              ) : (
                <p className="text-[10px] text-gray-600 font-medium">Alles gelesen</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1">
            {unread > 0 && (
              <button
                onClick={() => readAll.mutate()}
                disabled={readAll.isPending}
                className="text-[11px] font-bold text-violet-400 hover:text-violet-300 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-violet-500/10 active:scale-95 duration-150"
              >
                {readAll.isPending ? "…" : "Alle lesen"}
              </button>
            )}
            <CloseBtn onClick={toggleNotifDrawer} />
          </div>
        </div>

        {/* ── Notif count badge row ────────────────────────────────────────── */}
        {notifs.length > 0 && (
          <div
            className="flex items-center gap-2 px-5 py-2 flex-shrink-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: "rgba(0,0,0,0.2)" }}
          >
            {Object.entries(TYPE_META).map(([type, meta]) => {
              const count = notifs.filter((n) => n.type === type).length;
              if (!count) return null;
              return (
                <span
                  key={type}
                  className="text-[10px] font-black px-2 py-0.5 rounded-full"
                  style={{ background: `${meta.color}15`, color: meta.color, border: `1px solid ${meta.color}25` }}
                >
                  {meta.icon} {count}
                </span>
              );
            })}
          </div>
        )}

        {/* ── List ────────────────────────────────────────────────────────── */}
        <div className="overflow-y-auto flex-1 overscroll-contain" style={{ scrollbarWidth: "none" }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div
                className="w-6 h-6 rounded-full border-2 border-violet-500/30 border-t-violet-500"
                style={{ animation: "spin 0.7s linear infinite" }}
              />
            </div>
          ) : notifs.length === 0 ? (
            <EmptyState />
          ) : (
            notifs.map((n, idx) => (
              <NotifRow
                key={n.id}
                n={n}
                idx={idx}
                onMarkRead={(id) => markOne.mutate(id)}
              />
            ))
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        {notifs.length > 0 && (
          <div
            className="flex items-center justify-center px-5 py-3 flex-shrink-0"
            style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
          >
            <p className="text-[10px] text-gray-700 font-medium">
              {notifs.length} Benachrichtigung{notifs.length !== 1 ? "en" : ""} · Doppelklick zum Lesen
            </p>
          </div>
        )}
      </div>
    </>
  );
}
